import { createIsomorphicFn } from "@tanstack/react-start";
import { getRequestRuntime } from "~/lib/ssr/request-runtime.server";
import { nanoid } from "@repo/utils/nanoid";

type RouteVisitListener = (routeVisitId: string) => void;

interface RouteVisitRouterEvent {
  hrefChanged?: boolean;
}

interface RouteVisitRouter {
  subscribe(
    eventType: "onResolved",
    listener: (event: RouteVisitRouterEvent) => void,
  ): () => void;
}

const listeners = new Set<RouteVisitListener>();
let currentRouteVisitId: string | null = null;
let stopListeningToRouter: (() => void) | null = null;

function createRouteVisitId(): string {
  return `visit_${nanoid()}`;
}

function publishRouteVisitId(nextRouteVisitId: string) {
  currentRouteVisitId = nextRouteVisitId;
  for (const listener of listeners) {
    listener(nextRouteVisitId);
  }
}

export const getRouteVisitId = createIsomorphicFn()
  .server(() => getRequestRuntime().requestId)
  .client((): string => (currentRouteVisitId ??= createRouteVisitId()));

export function rotateRouteVisitId(): string {
  const nextRouteVisitId = createRouteVisitId();
  publishRouteVisitId(nextRouteVisitId);
  return nextRouteVisitId;
}

export function subscribeToRouteVisit(
  listener: RouteVisitListener,
): () => void {
  listeners.add(listener);
  listener(getRouteVisitId());

  return () => {
    listeners.delete(listener);
  };
}

export function initializeRouteVisit(router: RouteVisitRouter): void {
  if (stopListeningToRouter) {
    return;
  }

  stopListeningToRouter = router.subscribe("onResolved", (event) => {
    if (event.hrefChanged) {
      rotateRouteVisitId();
    }
  });
}

export function setRouteVisitIdForTests(routeVisitId: string): void {
  publishRouteVisitId(routeVisitId);
}

export function resetRouteVisitForTests(): void {
  stopListeningToRouter?.();
  stopListeningToRouter = null;
  listeners.clear();
  currentRouteVisitId = null;
}
